import { useEffect, useRef, useState } from "react";

import { useConsoleLayout } from "../../stores/console";
import { useStudioSettings } from "../../stores/studio";
import { CANVAS_HEIGHT, CANVAS_WIDTH } from "./layouts";
import { DeviceBar } from "./DeviceBar";
import { LayoutPicker } from "./LayoutPicker";
import { OverlayPanel } from "./OverlayPanel";
import { SceneEditor } from "./SceneEditor";
import { activeScene } from "./scene";
import { useStudioSession } from "./StudioSession";

/**
 * The browser studio, in a column of its own.
 *
 * ## Why it is in the console and not on a page of its own
 *
 * It was a page, briefly, and that was wrong. Hosting is not one task: it is
 * watching the queue, reading chat, reviewing what came in and skipping a track
 * that is not landing - all while on air. A studio on a separate page means
 * leaving the room to change a camera, which is the moment nobody is watching
 * the queue.
 *
 * ## And why it gets a whole column
 *
 * It briefly shared the deck's column, with its settings folded into a
 * disclosure to make room. That hid the half of the studio a host most wants to
 * play with - the layout, the background, which camera - behind a click, to fit
 * beside controls it has nothing to do with.
 *
 * The console now spends a column on the studio only when the studio is open,
 * and nothing on it when it is closed. So there is no shortage to design around
 * and every control is simply visible.
 *
 * ## Nothing here may unmount mid-broadcast
 *
 * The render loop, the devices and the peer connection all live in `useStudio`,
 * so this component disappearing takes the broadcast with it. That is why the
 * console opens it from a local toggle rather than from `room.source`: the room
 * is polled, and a single response that arrived while the status was rolling
 * over would otherwise drop a live show.
 */
export function StudioColumn() {
  const { deck, room, presets, credentials, missing } = useStudioSession();

  /*
   * Destructured, so everything below reads exactly as it did when these were
   * local `useState` values. Only the writes changed: they go through `set`,
   * because the studio's settings now outlive this component.
   */
  const {
    book,
    deckLevel,
    micLevel,
    camera,
    microphone,
    monitoring,
    set,
    applyPreset,
    updateLayer,
    removeLayer,
    setBackground,
  } = useStudioSettings();

  /*
   * The scene being edited, and the two layers this panel still has named
   * controls for.
   *
   * They are looked up by kind rather than by index because a scene need not
   * have either - "Artwork only" has no camera, and a host will eventually be
   * able to delete any of them. `undefined` here means the control is not
   * offered, which is the honest answer.
   */
  const scene = activeScene(book);
  const cardLayer = scene.layers.find((layer) => layer.kind === "card");
  const brandLayer = scene.layers.find((layer) => layer.kind === "brand");
  const background = scene.background;

  /**
   * How many consecutive seconds the outgoing mix has been silent.
   *
   * Local, unlike the settings: it is a property of *looking* at the studio,
   * and resetting it when the host navigates away and back is correct - they
   * have just arrived and should be told again if it is still wrong.
   */
  const [silence, setSilence] = useState(0);

  /*
   * Which half of the settings is showing, remembered.
   *
   * In the console store rather than in component state, because the faders
   * under `sound` are what a host adjusts mid-set: a tab that reset on every
   * navigation would hide the two most-used controls behind a click every
   * single time. See `studioTab` there for why the default is still `frame`.
   */
  /*
   * Which layer has handles on it.
   *
   * Local and not persisted: it is a property of what the host is pointing at,
   * and arriving with something selected from last week would put handles on
   * the frame for no reason anybody could remember.
   */
  const [selectedLayer, setSelectedLayer] = useState<string | null>(null);

  const tab = useConsoleLayout((s) => s.studioTab);
  const setTab = useConsoleLayout((s) => s.setStudioTab);

  /*
   * The level, readable from inside a timer without becoming its dependency.
   *
   * This is the whole fix for a warning that used to latch. See below.
   */
  const levelRef = useRef(deck.level);

  levelRef.current = deck.level;

  /*
   * Silence, counted rather than assumed.
   *
   * A broadcast carrying no audio looks exactly like a working one from behind
   * the desk: the connection is up, the preview moves, the timer counts. The
   * only place the difference shows is a meter that never leaves zero, and a
   * host mid-set is not staring at a meter.
   *
   * Eight seconds before it says so, because the first few after starting are
   * legitimately quiet - the deck has to fetch a manifest and fill a buffer
   * before a single sample exists - and warning during that would train people
   * to ignore it.
   *
   * Not during a reconnect: the mix is genuinely silent to the server while
   * there is no connection, and saying "nothing is going out" over the top of
   * "the connection dropped" is two alarms for one problem.
   *
   * ## Why the level is read from a ref
   *
   * Because it used to be a dependency, and that made this warning permanent
   * once it appeared - reported as "it never goes away, whether it corrects
   * itself or not".
   *
   * The meter updates ten times a second. With `deck.level` in the dependency
   * array, every one of those updates tore down this interval and started a new
   * one, so a timer that needs a full second to fire never got near it. The
   * counter simply stopped moving.
   *
   * The cruel part is that it worked perfectly while the mix was silent: `peak()`
   * returns exactly 0 every time, React bails out of a re-render for an
   * unchanged value, the effect stays put and the count climbs to eight. Sound
   * coming back is what started the thrashing - so the warning appeared
   * correctly, and then could never be cleared by the one thing that should
   * clear it.
   *
   * A ref keeps the timer stable and still reads the current value, so silence
   * is sampled once a second whatever the meter is doing.
   */
  useEffect(() => {
    if (deck.status !== "live") {
      setSilence(0);

      return;
    }

    const timer = setInterval(() => {
      setSilence((seconds) => (levelRef.current > 0.001 ? 0 : seconds + 1));
    }, 1000);

    return () => clearInterval(timer);
  }, [deck.status]);

  // The session fetches the room, so it can be absent for a moment on first
  // open. Nothing below reads well against a null room.
  if (!room) return <aside className="bg-app p-6" data-studio />;

  const ready = room.status === "live" && room.source === "stream";

  return (
    <aside
      className="flex min-w-0 flex-col gap-6 bg-app p-6 lg:min-h-0 lg:overflow-y-auto"
      data-studio
    >
      <div className="flex items-center justify-between">
        <span className="meta-caps text-faint">Studio</span>
        <span
          className={`font-mono text-[11px] ${
            deck.status === "reconnecting" ? "text-hot-text" : "text-faint"
          }`}
          data-studio-connection
        >
          {deck.status === "reconnecting"
            ? "reconnecting…"
            : deck.status === "live"
              ? deck.connection
              : "off air"}
        </span>
      </div>

      {/*
        The preview and the encoder's canvas.

        The second is 720p and never displayed - `hidden` rather than
        unmounted, because `captureStream` needs a canvas that is in the
        document and an unmounted one produces a stream of nothing.
      */}
      {/*
        `shrink-0`, or there is no preview.

        This is a flex column that scrolls, and the column got much taller when
        the studio gained its own controls. Flex children shrink by default, and
        an `aspect-video` box has a min-content height of nothing - so the
        preview quietly collapsed to a 1px line while the canvas kept its
        1280x720 backing store and carried on compositing perfectly. The stream
        was fine; the host just could not see it.
      */}
      <div className="relative shrink-0 overflow-hidden rounded-[var(--radius-panel)] border border-line bg-black">
        <canvas
          ref={deck.previewRef}
          width={CANVAS_WIDTH}
          height={CANVAS_HEIGHT}
          className="block aspect-video w-full"
          data-studio-preview
        />

        {/*
          The handles live over the canvas, never in it.

          This canvas is what `captureStream` is pulling from, so anything
          painted into it goes out live - a selection outline would be on every
          listener's screen. DOM in the same box is the only safe way to draw on
          top of a broadcast.
        */}
        <SceneEditor
          layers={scene.layers}
          selected={selectedLayer}
          /*
           * What has nothing to draw right now.
           *
           * The card paints only when something is playing and the camera only
           * once it is switched on, so before either happens those layers are
           * invisible - and a host cannot position what they cannot see. The
           * editor outlines them instead; the canvas stays exactly as it will
           * go out.
           */
          empty={
            new Set(
              [
                room?.now_playing ? null : ("card" as const),
                deck.cameraOn ? null : ("camera" as const),
              ].filter((kind): kind is "card" | "camera" => kind !== null),
            )
          }
          onSelect={setSelectedLayer}
          onChange={(id, box) => updateLayer(id, { box })}
          onRemove={(id) => {
            removeLayer(id);
            setSelectedLayer(null);
          }}
        />
      </div>

      {missing && (
        <p className="rounded-[var(--radius-control)] border border-line px-3 py-2.5 font-mono text-[11px] leading-relaxed text-ghost">
          the studio is not switched on for this platform yet
        </p>
      )}

      {deck.error && (
        <p
          role="alert"
          data-studio-error
          className="rounded-[var(--radius-control)] border border-hot-edge bg-hot-wash px-3 py-2.5 font-mono text-[11px] leading-relaxed text-hot-text"
        >
          {deck.error}
        </p>
      )}

      {(deck.status === "live" || deck.status === "reconnecting") && (
        <Meter level={deck.level} />
      )}

      {deck.status === "reconnecting" && (
        <p
          data-studio-reconnecting
          className="rounded-[var(--radius-control)] border border-hot-edge bg-hot-wash px-3 py-2.5 font-mono text-[11px] leading-relaxed text-hot-text"
        >
          the connection dropped — getting it back. your camera and mix are
          still running, so this picks up where it left off.
        </p>
      )}

      {silence >= 8 && (
        <p
          data-studio-silent
          className="rounded-[var(--radius-control)] border border-hot-edge bg-hot-wash px-3 py-2.5 font-mono text-[11px] leading-relaxed text-hot-text"
        >
          nothing is going out — the mix has been silent for {silence}s. check
          the deck is playing and the music level is up.
        </p>
      )}

      {/*
        No start button, and no go-live button.

        Both used to live here, which meant the host pressed "Go on air" in the
        header, found the room live but silent, and had to press a differently
        worded button in this column to actually broadcast. The header owns the
        action now and the session publishes as soon as the room is in studio
        mode - so this panel reports rather than asks.

        The exception is a failure: something has to offer another go, and it
        belongs next to the error that explains why.
      */}
      {deck.status === "error" && (
        <button
          type="button"
          disabled={!credentials}
          onClick={() => credentials && void deck.start(credentials.whip)}
          data-studio-retry
          className="rounded-[var(--radius-control)] bg-accent py-3 text-[13px] font-bold text-accent-ink transition-colors hover:bg-accent-hover disabled:cursor-not-allowed disabled:opacity-50"
        >
          Try again
        </button>
      )}

      {!ready && deck.status !== "error" && (
        <p className="font-mono text-[10px] leading-relaxed text-ghost">
          {room.status === "live"
            ? "this room is on air from the catalogue — switch it to Studio above"
            : "set your camera and mix up here, then go on air"}
        </p>
      )}

      {/* ------------------------------------------------------------------ */}

      {/*
        Camera and microphone, on one strip directly under the preview.

        They were two labelled groups with four full-width rows between them,
        which is a lot of a narrow column spent on the two switches a host sets
        once and then leaves alone - while the mix faders they actually reach
        for mid-set were pushed off the bottom.
      */}
      <DeviceBar
        camera={camera}
        microphone={microphone}
        cameras={deck.devices.cameras}
        microphones={deck.devices.microphones}
        cameraOn={deck.cameraOn}
        micOn={deck.micOn}
        onToggleCamera={() =>
          deck.cameraOn
            ? deck.stopCamera()
            : void deck.startCamera(camera || undefined)
        }
        onToggleMic={() =>
          deck.micOn
            ? deck.stopMic()
            : void deck.startMic(microphone || undefined)
        }
        onPickCamera={(id) => {
          set("camera", id);
          // Switched live, because a host discovering they are on the wrong
          // camera wants it fixed now, not after a restart.
          if (deck.cameraOn) void deck.startCamera(id || undefined);
        }}
        onPickMic={(id) => {
          set("microphone", id);
          if (deck.micOn) void deck.startMic(id || undefined);
        }}
      />

      {deck.passthrough && (
        <p
          data-studio-passthrough
          className="rounded-[var(--radius-control)] border border-fresh-edge bg-fresh-wash px-3 py-2.5 font-mono text-[11px] leading-relaxed text-fresh"
        >
          passthrough — your camera goes out untouched, nothing drawn over it.
          use this with OBS's virtual camera. turn on an overlay to composite
          again.
        </p>
      )}

      {/*
        Two tabs, because there are two jobs.
        
        This was six equally-weighted sections stacked down a narrow column -
        layout, on screen, background, images, mix, monitor - each with its own
        heading and its own explanatory line. Every one of them looked as
        important as every other, so finding the fader you wanted mid-set meant
        reading all six headings, and the column ran well past the fold.
        
        A host is only ever doing one of two things here: arranging the picture,
        or setting up the sound. Splitting on that halves the height and puts a
        name on what each half is for.
        
        The device strip stays outside the tabs, because the camera and the
        microphone belong to both and are the two switches most likely to be
        wanted in a hurry.
      */}
      <div
        className="flex gap-1 rounded-[var(--radius-control)] bg-deep p-1"
        role="tablist"
        aria-label="Studio settings"
      >
        {(["frame", "sound"] as const).map((id) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={tab === id}
            onClick={() => setTab(id)}
            data-studio-tab={id}
            className={`flex-1 rounded-[calc(var(--radius-control)-2px)] py-1.5 text-[12px] font-bold capitalize transition-colors ${
              tab === id
                ? "bg-raised text-ink"
                : "text-faint hover:text-soft"
            }`}
          >
            {id}
          </button>
        ))}
      </div>

      {tab === "frame" ? (
        <>
          <Group label="Layout">
            <LayoutPicker
              selected={scene.presetId}
              onPick={(id) => applyPreset(id)}
            />

            {/*
              The two things a preset puts on the frame, as chips rather than
              full-width rows with their own heading. They belong to the layout
              above - they are parts of it being switched off - and a section
              called "On screen" sitting between the layout and the background
              suggested they were a third, separate idea.
            */}
            <div className="mt-2.5 flex flex-wrap gap-1.5">
              {cardLayer && (
                <Chip
                  on={cardLayer.visible}
                  onClick={() =>
                    updateLayer(cardLayer.id, { visible: !cardLayer.visible })
                  }
                  hook="card"
                >
                  Now playing
                </Chip>
              )}
              {brandLayer && (
                <Chip
                  on={brandLayer.visible}
                  onClick={() =>
                    updateLayer(brandLayer.id, { visible: !brandLayer.visible })
                  }
                  hook="brand"
                >
                  Your name
                </Chip>
              )}
            </div>
          </Group>

          <Group label="Background">
            <div className="flex flex-wrap gap-2">
              {presets.map((preset) => (
                <button
                  key={preset.preset}
                  type="button"
                  onClick={() => setBackground(preset)}
                  aria-pressed={background?.preset === preset.preset}
                  aria-label={preset.label}
                  title={preset.label}
                  style={{
                    background: `linear-gradient(${preset.angle}deg, ${preset.stops.join(", ")})`,
                  }}
                  className={`h-7 w-7 rounded-full border-2 transition-colors ${
                    background?.preset === preset.preset
                      ? "border-ink"
                      : "border-transparent"
                  }`}
                />
              ))}
            </div>
          </Group>

          <Group label="Images">
            <OverlayPanel
              selected={selectedLayer}
              onSelect={setSelectedLayer}
            />
          </Group>
        </>
      ) : (
        <>
          <Group label="Mix">
            <Slider
              label="Music"
              value={deckLevel}
              onChange={(value) => set("deckLevel", value)}
            />
            <Slider
              label="Microphone"
              value={micLevel}
              onChange={(value) => set("micLevel", value)}
            />
            <p className="mt-1 font-mono text-[10px] leading-relaxed text-ghost">
              the balance of what goes out — what you hear does not change it
            </p>
          </Group>

          <Group label="Monitor">
            <Check
              label="Hear the deck"
              on={monitoring}
              onChange={(value) => set("monitoring", value)}
            />
            <p className="mt-1 font-mono text-[10px] leading-relaxed text-ghost">
              your speakers only — the music keeps going out either way, so you
              can talk over it without hearing yourself
            </p>
          </Group>
        </>
      )}
    </aside>
  );
}

/* -------------------------------------------------------------------------- */

function Group({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <p className="meta-caps mb-2.5 text-ghost">{label}</p>
      {children}
    </div>
  );
}

/**
 * A small on/off pill, for the parts of a layout a host can switch off.
 *
 * Not a `Check`: those are full-width rows with the label at one end and a box
 * at the other, which is right for a list of independent settings and wrong for
 * two related things that belong under the layout they are part of. Two rows
 * there read as a separate section; two chips read as options on the thing
 * above them.
 */
function Chip({
  on,
  onClick,
  hook,
  children,
}: {
  on: boolean;
  onClick: () => void;
  hook: string;
  children: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={on}
      data-studio-chip={hook}
      className={`rounded-[var(--radius-pill)] border px-2.5 py-1 text-[11px] transition-colors ${
        on
          ? "border-transparent bg-raised text-ink"
          : "border-line text-faint hover:border-line-lit hover:text-soft"
      }`}
    >
      {children}
    </button>
  );
}

function Check({
  label,
  on,
  onChange,
}: {
  label: string;
  on: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <label className="flex cursor-pointer items-center justify-between py-1.5 text-[13px] text-ink">
      {label}
      <input
        type="checkbox"
        checked={on}
        onChange={(e) => onChange(e.target.checked)}
        className="h-4 w-4 accent-[var(--color-accent)]"
      />
    </label>
  );
}

function Slider({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number;
  onChange: (next: number) => void;
}) {
  return (
    <label className="block py-1 text-[13px] text-ink">
      <span className="mb-1 flex items-center justify-between">
        {label}
        <span className="font-mono text-[10px] text-faint">
          {Math.round(value * 100)}
        </span>
      </span>
      <input
        type="range"
        min={0}
        max={1}
        step={0.01}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full accent-[var(--color-accent)]"
      />
    </label>
  );
}

/**
 * What is going out, right now.
 *
 * The connection state lives in the column header rather than here, because the
 * two answer the same question - "is anything reaching anybody?" - and a moving
 * meter on a failed connection is a host talking to nobody.
 */
function Meter({ level }: { level: number }) {
  const width = Math.min(100, Math.round(level * 140));

  return (
    <div className="flex items-center gap-3" data-studio-meter>
      <span className="meta-caps text-ghost">out</span>
      <div className="h-2 flex-1 overflow-hidden rounded-full bg-raised">
        <div
          className={`h-full transition-[width] duration-100 ${width > 92 ? "bg-hot" : "bg-fresh"}`}
          style={{ width: `${width}%` }}
        />
      </div>
    </div>
  );
}
